import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getAgreements } from 'app/entities/agreement/agreement.reducer';
import { InvoiceStatus } from 'app/shared/model/enumerations/invoice-status.model';
import { InvoiceType } from 'app/shared/model/enumerations/invoice-type.model';
import { createEntity, getEntity, reset, updateEntity } from './invoice.reducer';

export const InvoiceUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const agreements = useAppSelector(state => state.agreement.entities);
  const invoiceEntity = useAppSelector(state => state.invoice.entity);
  const loading = useAppSelector(state => state.invoice.loading);
  const updating = useAppSelector(state => state.invoice.updating);
  const updateSuccess = useAppSelector(state => state.invoice.updateSuccess);
  const invoiceStatusValues = Object.keys(InvoiceStatus);
  const invoiceTypeValues = Object.keys(InvoiceType);

  const handleClose = () => {
    navigate('/invoice');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getAgreements({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.sumFixedMonthlyPrice !== undefined && typeof values.sumFixedMonthlyPrice !== 'number') {
      values.sumFixedMonthlyPrice = Number(values.sumFixedMonthlyPrice);
    }
    values.createdTimestamp = convertDateTimeToServer(values.createdTimestamp);

    const entity = {
      ...invoiceEntity,
      ...values,
      agreement: agreements.find(it => it.id.toString() === values.agreement?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdTimestamp: displayDefaultDateTime(),
        }
      : {
          invoiceStatus: 'OUTSTANDING',
          invoiceType: 'MONTH',
          ...invoiceEntity,
          createdTimestamp: convertDateTimeFromServer(invoiceEntity.createdTimestamp),
          agreement: invoiceEntity?.agreement?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.invoice.home.createOrEditLabel" data-cy="InvoiceCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.invoice.home.createOrEditLabel">
              Create or edit a Invoice
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="invoice-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.sumFixedMonthlyPrice')}
                id="invoice-sumFixedMonthlyPrice"
                name="sumFixedMonthlyPrice"
                data-cy="sumFixedMonthlyPrice"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.startDate')}
                id="invoice-startDate"
                name="startDate"
                data-cy="startDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.endDate')}
                id="invoice-endDate"
                name="endDate"
                data-cy="endDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.invoiceStatus')}
                id="invoice-invoiceStatus"
                name="invoiceStatus"
                data-cy="invoiceStatus"
                type="select"
              >
                {invoiceStatusValues.map(invoiceStatus => (
                  <option value={invoiceStatus} key={invoiceStatus}>
                    {translate(`crawlerBackendApp.InvoiceStatus.${invoiceStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.invoiceType')}
                id="invoice-invoiceType"
                name="invoiceType"
                data-cy="invoiceType"
                type="select"
              >
                {invoiceTypeValues.map(invoiceType => (
                  <option value={invoiceType} key={invoiceType}>
                    {translate(`crawlerBackendApp.InvoiceType.${invoiceType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.createdTimestamp')}
                id="invoice-createdTimestamp"
                name="createdTimestamp"
                data-cy="createdTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.invoiceNumber')}
                id="invoice-invoiceNumber"
                name="invoiceNumber"
                data-cy="invoiceNumber"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.relation')}
                id="invoice-relation"
                name="relation"
                data-cy="relation"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.invoice.description')}
                id="invoice-description"
                name="description"
                data-cy="description"
                type="text"
              />
              <ValidatedField
                id="invoice-agreement"
                name="agreement"
                data-cy="agreement"
                label={translate('crawlerBackendApp.invoice.agreement')}
                type="select"
              >
                <option value="" key="0" />
                {agreements
                  ? agreements.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/invoice" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InvoiceUpdate;
